import { useState, type MouseEvent } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import LanguageIcon from '@mui/icons-material/Language';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/app/authStore';
import { useRealtimeStore } from '@/hooks/useWebSocket';
import { changeLocale, type SupportedLocale } from '@/i18n';

interface Props {
  drawerWidth: number;
  onToggleNav: () => void;
}

export default function AppHeader({ drawerWidth, onToggleNav }: Props) {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const connection = useRealtimeStore((state) => state.status);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const openMenu = (event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget);
  const closeMenu = () => setAnchor(null);

  const switchLocale = () => {
    const next: SupportedLocale = i18n.language === 'ru' ? 'en' : 'ru';
    changeLocale(next);
    closeMenu();
  };

  const initials = (user?.fullName ?? '?')
    .split(' ')
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase();

  const ConnectionIcon =
    connection === 'connected'
      ? CloudDoneIcon
      : connection === 'connecting'
        ? CloudSyncIcon
        : CloudOffIcon;

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          edge="start"
          onClick={onToggleNav}
          sx={{ mr: 2, display: { md: 'none' } }}
          aria-label="menu"
        >
          <MenuIcon />
        </IconButton>

        <Box className="flex-1" />

        {/* the connection indicator is what tells a user whether live updates are arriving */}
        <Tooltip title={t(`realtime.${connection}`)}>
          <Box className="mr-2 flex items-center" aria-label={`realtime-${connection}`}>
            <ConnectionIcon
              fontSize="small"
              color={connection === 'connected' ? 'inherit' : 'disabled'}
            />
          </Box>
        </Tooltip>

        <Tooltip title={user?.fullName ?? ''}>
          <IconButton onClick={openMenu} size="small" aria-label="account">
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
              <Typography variant="caption">{initials}</Typography>
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={closeMenu}>
          <Box className="px-4 py-2">
            <Typography variant="subtitle2">{user?.fullName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.roles.map((role) => t(`auth.roles.${role}`)).join(', ')}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={switchLocale}>
            <ListItemIcon>
              <LanguageIcon fontSize="small" />
            </ListItemIcon>
            {i18n.language === 'ru' ? 'English' : 'Russian'}
          </MenuItem>
          <MenuItem
            onClick={() => {
              closeMenu();
              void logout();
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            {t('auth.signOut')}
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
